import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, where, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import {
  BarChart3, Users, ShoppingCart, Building2, FileText, Handshake,
  TrendingUp, TrendingDown, Calendar, RefreshCw, UserPlus, Activity,
  Package, Briefcase, MessageCircle, DollarSign
} from 'lucide-react';

function AdminAnalytics() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('month'); // today, week, month

  useEffect(() => { loadStats(); }, []);

  const getDateRange = (p) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (p === 'today') return { start: today, end: now };
    if (p === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      return { start: weekAgo, end: now };
    }
    // month
    const monthAgo = new Date(today);
    monthAgo.setMonth(monthAgo.getMonth() - 1);
    return { start: monthAgo, end: now };
  };

  const loadStats = async () => {
    try {
      setLoading(true);

      // === 1. 사용자 현황 ===
      let allUsers = [];
      try {
        const usersSnap = await getDocs(collection(db, 'users'));
        allUsers = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (e) { console.warn('users read skip:', e.message); }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);

      const monthAgo = new Date();
      monthAgo.setMonth(monthAgo.getMonth() - 1);

      const usersByType = {
        // 구버전 SignUp.js 가입자는 businessType='clinic', 신버전 Register.js는 'dental' — 둘 다 치과로 집계
        dental: allUsers.filter(u => (u.businessType === 'dental' || u.businessType === 'clinic') && u.userType !== 'staff'),
        lab: allUsers.filter(u => u.businessType === 'lab' && u.userType !== 'staff'),
        outsource: allUsers.filter(u => u.businessType === 'outsource'),
        staff: allUsers.filter(u => u.userType === 'staff'),
        individual: allUsers.filter(u => u.userType === 'individual' || (!u.businessType && u.userType !== 'staff' && u.userType !== 'admin')),
      };

      // 오늘 가입자
      const todaySignups = allUsers.filter(u => {
        const created = u.createdAt?.toDate?.() || (u.createdAt ? new Date(u.createdAt) : null);
        return created && created >= today;
      });

      // 이번 주 가입자
      const weekSignups = allUsers.filter(u => {
        const created = u.createdAt?.toDate?.() || (u.createdAt ? new Date(u.createdAt) : null);
        return created && created >= weekAgo;
      });

      // 이번 달 가입자
      const monthSignups = allUsers.filter(u => {
        const created = u.createdAt?.toDate?.() || (u.createdAt ? new Date(u.createdAt) : null);
        return created && created >= monthAgo;
      });

      // 최근 7일 활성 (lastLoginAt 또는 updatedAt)
      const activeUsers = allUsers.filter(u => {
        const lastActive = u.lastLoginAt?.toDate?.() || (u.updatedAt ? new Date(u.updatedAt) : null);
        return lastActive && lastActive >= weekAgo;
      });

      // 월별 가입 추이 (최근 6개월)
      const monthlySignups = [];
      for (let i = 5; i >= 0; i--) {
        const d = new Date();
        d.setMonth(d.getMonth() - i);
        const year = d.getFullYear();
        const month = d.getMonth();
        const count = allUsers.filter(u => {
          const created = u.createdAt?.toDate?.() || (u.createdAt ? new Date(u.createdAt) : null);
          return created && created.getFullYear() === year && created.getMonth() === month;
        }).length;
        monthlySignups.push({ label: `${month + 1}월`, count });
      }

      // === 2. 주문 현황 ===
      let allOrders = [];
      try {
        const ordersSnap = await getDocs(collection(db, 'workOrders'));
        allOrders = ordersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (e) { console.warn('workOrders read skip:', e.message); }

      const todayOrders = allOrders.filter(o => {
        const created = o.createdAt?.toDate?.() || (o.createdAt ? new Date(o.createdAt) : null);
        return created && created >= today;
      });

      const monthOrders = allOrders.filter(o => {
        const created = o.createdAt?.toDate?.() || (o.createdAt ? new Date(o.createdAt) : null);
        return created && created >= monthAgo;
      });

      const ordersByStatus = {
        pending: allOrders.filter(o => o.status === 'pending' || o.status === '대기').length,
        inProgress: allOrders.filter(o => o.status === 'in_progress' || o.status === '진행중' || o.status === 'accepted').length,
        completed: allOrders.filter(o => o.status === 'completed' || o.status === '완료').length,
      };

      // 월별 주문 추이
      const monthlyOrders = [];
      for (let i = 5; i >= 0; i--) {
        const d = new Date();
        d.setMonth(d.getMonth() - i);
        const year = d.getFullYear();
        const month = d.getMonth();
        const count = allOrders.filter(o => {
          const created = o.createdAt?.toDate?.() || (o.createdAt ? new Date(o.createdAt) : null);
          return created && created.getFullYear() === year && created.getMonth() === month;
        }).length;
        monthlyOrders.push({ label: `${month + 1}월`, count });
      }

      // === 3. 중개 현황 ===
      let matchRequests = [];
      try {
        const matchSnap = await getDocs(collection(db, 'matchRequests'));
        matchRequests = matchSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (e) { console.log('matchRequests skip'); }

      const monthMatches = matchRequests.filter(m => {
        const created = m.createdAt?.toDate?.() || (m.createdAt ? new Date(m.createdAt) : null);
        return created && created >= monthAgo;
      });

      const matchCompleted = matchRequests.filter(m => m.status === 'completed' || m.status === 'matched');

      // === 4. 거래 현황 ===
      let statements = [];
      try {
        const stmtSnap = await getDocs(collection(db, 'transactionStatements'));
        statements = stmtSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (e) { console.log('statements skip'); }

      const monthStatements = statements.filter(s => {
        const created = s.createdAt?.toDate?.() || (s.createdAt ? new Date(s.createdAt) : null);
        return created && created >= monthAgo;
      });

      const totalAmount = statements.reduce((sum, s) => sum + (parseInt(s.supplyAmount) || 0), 0);
      const monthAmount = monthStatements.reduce((sum, s) => sum + (parseInt(s.supplyAmount) || 0), 0);

      // === 5. 콘텐츠 현황 ===
      let labAds = [], usedItems = [], jobs = [], seminars = [], feedbacks = [];
      try {
        const labAdSnap = await getDocs(collection(db, 'labAdvertisements'));
        labAds = labAdSnap.docs.map(d => d.data());
      } catch (e) {}
      try {
        const usedSnap = await getDocs(collection(db, 'usedItems'));
        usedItems = usedSnap.docs.map(d => d.data());
      } catch (e) {}
      try {
        const jobSnap = await getDocs(collection(db, 'jobPostings'));
        jobs = jobSnap.docs.map(d => d.data());
      } catch (e) {}
      try {
        const semSnap = await getDocs(collection(db, 'seminars'));
        seminars = semSnap.docs.map(d => d.data());
      } catch (e) {}
      try {
        const fbSnap = await getDocs(collection(db, 'feedbacks'));
        feedbacks = fbSnap.docs.map(d => d.data());
      } catch (e) {}

      // === 6. 방문자 현황 ===
      let visitLogs = [];
      try {
        const visitSnap = await getDocs(collection(db, 'visitLogs'));
        visitLogs = visitSnap.docs.map(d => d.data());
      } catch (e) { console.log('visitLogs skip:', e.message); }

      const todayVisits = visitLogs.filter(v => v.date === todayStr);
      const todayUniqueVisitors = [...new Set(todayVisits.map(v => v.userId))].length;
      const weekVisits = visitLogs.filter(v => {
        const d = new Date(v.date);
        return d >= weekAgo;
      });
      const weekUniqueVisitors = [...new Set(weekVisits.map(v => v.userId))].length;

      // 플랫폼별
      const webVisitsToday = todayVisits.filter(v => v.platform === 'web').length;
      const appVisitsToday = todayVisits.filter(v => v.platform === 'app').length;

      // 최근 7일 일별 방문자
      const dailyVisitors = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        const dayVisits = visitLogs.filter(v => v.date === dateStr);
        const unique = [...new Set(dayVisits.map(v => v.userId))].length;
        dailyVisitors.push({ label: `${d.getMonth() + 1}/${d.getDate()}`, count: unique, total: dayVisits.length });
      }

      // === 7. 거래처 연결 ===
      let connections = [];
      try {
        const connSnap = await getDocs(collection(db, 'connections'));
        connections = connSnap.docs.map(d => d.data());
      } catch (e) {}

      // === 8. 랜딩 페이지 유입(promoVisits) ===
      let promoVisits = [];
      try {
        const pvSnap = await getDocs(collection(db, 'promoVisits'));
        promoVisits = pvSnap.docs.map(d => d.data());
      } catch (e) { console.log('promoVisits skip:', e.message); }

      const labRecruitVisits = promoVisits.filter(v => v.page === 'lab-recruit');
      const labRecruitToday = labRecruitVisits.filter(v => v.date === todayStr).length;
      const labRecruitWeek = labRecruitVisits.filter(v => { const d = new Date(v.date); return d >= weekAgo; }).length;
      const labRecruitDaily = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const ds = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        labRecruitDaily.push({ label: `${d.getMonth()+1}/${d.getDate()}`, count: labRecruitVisits.filter(v => v.date === ds).length });
      }
      const labRecruitBySource = {};
      labRecruitVisits.forEach(v => {
        const s = v.source || v.utmSource || 'direct';
        labRecruitBySource[s] = (labRecruitBySource[s] || 0) + 1;
      });
      const labRecruitSourceList = Object.entries(labRecruitBySource)
        .map(([source, count]) => ({ source, count }))
        .sort((a, b) => b.count - a.count);

      // 치과 랜딩 (clinic-main) 집계
      const clinicMainVisits = promoVisits.filter(v => v.page === 'clinic-main');
      const clinicMainToday = clinicMainVisits.filter(v => v.date === todayStr).length;
      const clinicMainWeek = clinicMainVisits.filter(v => { const d = new Date(v.date); return d >= weekAgo; }).length;
      const clinicMainDaily = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const ds = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        clinicMainDaily.push({ label: `${d.getMonth()+1}/${d.getDate()}`, count: clinicMainVisits.filter(v => v.date === ds).length });
      }
      const clinicMainBySource = {};
      clinicMainVisits.forEach(v => {
        const s = v.source || v.utmSource || 'direct';
        clinicMainBySource[s] = (clinicMainBySource[s] || 0) + 1;
      });
      const clinicMainSourceList = Object.entries(clinicMainBySource)
        .map(([source, count]) => ({ source, count }))
        .sort((a, b) => b.count - a.count);

      setStats({
        users: {
          total: allUsers.length,
          dental: usersByType.dental.length,
          lab: usersByType.lab.length,
          outsource: usersByType.outsource.length,
          staff: usersByType.staff.length,
          individual: usersByType.individual.length,
          todaySignups: todaySignups.length,
          weekSignups: weekSignups.length,
          monthSignups: monthSignups.length,
          activeUsers: activeUsers.length,
          monthlySignups,
        },
        orders: {
          total: allOrders.length,
          today: todayOrders.length,
          month: monthOrders.length,
          ...ordersByStatus,
          monthlyOrders,
        },
        brokerage: {
          total: matchRequests.length,
          month: monthMatches.length,
          completed: matchCompleted.length,
          rate: matchRequests.length > 0 ? Math.round((matchCompleted.length / matchRequests.length) * 100) : 0,
        },
        transactions: {
          total: statements.length,
          month: monthStatements.length,
          totalAmount,
          monthAmount,
          connections: connections.length,
        },
        visits: {
          todayTotal: todayVisits.length,
          todayUnique: todayUniqueVisitors,
          weekUnique: weekUniqueVisitors,
          webToday: webVisitsToday,
          appToday: appVisitsToday,
          dailyVisitors,
        },
        promo: {
          labRecruitTotal: labRecruitVisits.length,
          labRecruitToday,
          labRecruitWeek,
          labRecruitDaily,
          labRecruitSourceList,
          clinicMainTotal: clinicMainVisits.length,
          clinicMainToday,
          clinicMainWeek,
          clinicMainDaily,
          clinicMainSourceList,
        },
        content: {
          labAds: labAds.length,
          labAdsActive: labAds.filter(l => l.isActive).length,
          usedItems: usedItems.length,
          jobs: jobs.length,
          seminars: seminars.length,
          feedbacks: feedbacks.length,
        },
      });
    } catch (e) {
      console.error('Stats load error:', e);
      setError(e.message || '데이터 로드 실패');
    }
    setLoading(false);
  };

  const fmtNum = (n) => (n || 0).toLocaleString();
  const fmtAmount = (n) => {
    if (!n) return '0원';
    if (n >= 100000000) return `${(n / 100000000).toFixed(1)}억원`;
    if (n >= 10000) return `${Math.floor(n / 10000).toLocaleString()}만원`;
    return `${n.toLocaleString()}원`;
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>데이터 집계 중...</div>;
  if (!stats) return (
    <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
      <p>데이터를 불러올 수 없습니다.</p>
      {error && <p style={{ fontSize: 12, color: '#ef4444' }}>{error}</p>}
      <button onClick={loadStats} style={{ ...S.refreshBtn, margin: '10px auto' }}><RefreshCw size={16} /> 다시 시도</button>
    </div>
  );

  const maxMonthlySignup = Math.max(...stats.users.monthlySignups.map(m => m.count), 1);
  const maxMonthlyOrder = Math.max(...stats.orders.monthlyOrders.map(m => m.count), 1);
  const maxDailyVisitor = Math.max(...(stats.visits?.dailyVisitors || []).map(d => d.count), 1);

  return (
    <div style={{ padding: 20, maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 700 }}>플랫폼 분석</h1>
          <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>덴탈클러스터 운영 현황</p>
        </div>
        <button onClick={loadStats} style={S.refreshBtn}><RefreshCw size={16} /> 새로고침</button>
      </div>

      {/* ===== 0. 방문자 현황 ===== */}
      <div style={S.cardGrid2}>
        <div style={S.card}>
          <h3 style={S.cardTitle}><Activity size={18} /> 오늘 방문자</h3>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
            <div style={{ ...S.miniCard, flex: 1 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#6366f1' }}>{stats.visits?.todayUnique || 0}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>순 방문자</div>
            </div>
            <div style={{ ...S.miniCard, flex: 1 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>{stats.visits?.todayTotal || 0}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>총 접속</div>
            </div>
            <div style={{ ...S.miniCard, flex: 1 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#3b82f6' }}>{stats.visits?.webToday || 0}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>웹</div>
            </div>
            <div style={{ ...S.miniCard, flex: 1 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#10b981' }}>{stats.visits?.appToday || 0}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>앱</div>
            </div>
          </div>
          <div style={{ padding: '8px 12px', backgroundColor: '#f8fafc', borderRadius: 8, fontSize: 12 }}>
            최근 7일 순 방문자: <b>{stats.visits?.weekUnique || 0}명</b>
          </div>
        </div>

        <div style={S.card}>
          <h3 style={S.cardTitle}><TrendingUp size={18} /> 일별 방문자 추이 (7일)</h3>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 160, padding: '10px 0' }}>
            {(stats.visits?.dailyVisitors || []).map((d, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#6366f1' }}>{d.count}</span>
                <div style={{ width: '100%', backgroundColor: '#6366f1', borderRadius: '4px 4px 0 0', height: `${Math.max((d.count / maxDailyVisitor) * 120, 4)}px`, transition: 'height .3s' }} />
                <span style={{ fontSize: 10, color: '#94a3b8' }}>{d.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== 0-1. 랜딩 페이지 유입 (기공소 모집) ===== */}
      <div style={S.cardGrid2}>
        <div style={S.card}>
          <h3 style={S.cardTitle}><Activity size={18} /> 기공소 모집 랜딩 유입</h3>
          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 10 }}>/promo/lab-recruit · 비로그인 방문자 포함</div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ ...S.miniCard, flex: 1 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#f59e0b' }}>{stats.promo?.labRecruitToday || 0}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>오늘</div>
            </div>
            <div style={{ ...S.miniCard, flex: 1 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#6366f1' }}>{stats.promo?.labRecruitWeek || 0}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>7일</div>
            </div>
            <div style={{ ...S.miniCard, flex: 1 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>{stats.promo?.labRecruitTotal || 0}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>누적</div>
            </div>
          </div>
        </div>

        <div style={S.card}>
          <h3 style={S.cardTitle}><TrendingUp size={18} /> 랜딩 일별 유입 (7일)</h3>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 160, padding: '10px 0' }}>
            {(() => {
              const daily = stats.promo?.labRecruitDaily || [];
              const maxV = Math.max(...daily.map(d => d.count), 1);
              return daily.map((d, i) => (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b' }}>{d.count}</span>
                  <div style={{ width: '100%', backgroundColor: '#f59e0b', borderRadius: '4px 4px 0 0', height: `${Math.max((d.count / maxV) * 120, 4)}px`, transition: 'height .3s' }} />
                  <span style={{ fontSize: 10, color: '#94a3b8' }}>{d.label}</span>
                </div>
              ));
            })()}
          </div>
        </div>
      </div>

      {/* ===== 0-2. 매체별 유입 (UTM 소스) — 기공소 랜딩 ===== */}
      <div style={S.card}>
        <h3 style={S.cardTitle}><Activity size={18} /> 매체별 랜딩 유입 · 기공소 모집 (utm_source)</h3>
        <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 10 }}>누적 기준 · `direct`는 UTM 없이 직접 방문</div>
        {(() => {
          const list = stats.promo?.labRecruitSourceList || [];
          if (list.length === 0) return <div style={{ padding: 20, color: '#94a3b8', fontSize: 13 }}>아직 유입 기록이 없습니다.</div>;
          const maxV = Math.max(...list.map(s => s.count), 1);
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {list.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 120, fontSize: 12, fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.source}</div>
                  <div style={{ flex: 1, height: 20, background: '#f1f5f9', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{ width: `${(s.count / maxV) * 100}%`, height: '100%', background: 'linear-gradient(90deg, #6366f1, #8b5cf6)', transition: 'width .3s' }} />
                  </div>
                  <div style={{ width: 60, textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#6366f1' }}>{s.count}</div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* ===== 0-3. 치과 모집 랜딩 유입 (/promo/clinic) ===== */}
      <div style={S.cardGrid2}>
        <div style={S.card}>
          <h3 style={S.cardTitle}><Activity size={18} /> 치과 모집 랜딩 유입</h3>
          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 10 }}>/promo/clinic · 팩스·이메일 캠페인</div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ ...S.miniCard, flex: 1 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#f59e0b' }}>{stats.promo?.clinicMainToday || 0}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>오늘</div>
            </div>
            <div style={{ ...S.miniCard, flex: 1 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#6366f1' }}>{stats.promo?.clinicMainWeek || 0}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>7일</div>
            </div>
            <div style={{ ...S.miniCard, flex: 1 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>{stats.promo?.clinicMainTotal || 0}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>누적</div>
            </div>
          </div>
        </div>

        <div style={S.card}>
          <h3 style={S.cardTitle}><TrendingUp size={18} /> 치과 랜딩 일별 유입 (7일)</h3>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 160, padding: '10px 0' }}>
            {(() => {
              const daily = stats.promo?.clinicMainDaily || [];
              const maxV = Math.max(...daily.map(d => d.count), 1);
              return daily.map((d, i) => (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#10b981' }}>{d.count}</span>
                  <div style={{ width: '100%', backgroundColor: '#10b981', borderRadius: '4px 4px 0 0', height: `${Math.max((d.count / maxV) * 120, 4)}px`, transition: 'height .3s' }} />
                  <span style={{ fontSize: 10, color: '#94a3b8' }}>{d.label}</span>
                </div>
              ));
            })()}
          </div>
        </div>
      </div>

      <div style={S.card}>
        <h3 style={S.cardTitle}><Activity size={18} /> 매체별 랜딩 유입 · 치과 모집 (utm_source)</h3>
        <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 10 }}>누적 기준 · `fax`/`email`/`direct`로 분류</div>
        {(() => {
          const list = stats.promo?.clinicMainSourceList || [];
          if (list.length === 0) return <div style={{ padding: 20, color: '#94a3b8', fontSize: 13 }}>아직 유입 기록이 없습니다.</div>;
          const maxV = Math.max(...list.map(s => s.count), 1);
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {list.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 120, fontSize: 12, fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.source}</div>
                  <div style={{ flex: 1, height: 20, background: '#f1f5f9', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{ width: `${(s.count / maxV) * 100}%`, height: '100%', background: 'linear-gradient(90deg, #10b981, #059669)', transition: 'width .3s' }} />
                  </div>
                  <div style={{ width: 60, textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#10b981' }}>{s.count}</div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* ===== 1. 요약 카드 ===== */}
      <div style={S.cardGrid4}>
        <div style={S.summaryCard}>
          <div style={{ ...S.summaryIcon, backgroundColor: '#eef2ff' }}><Users size={22} color="#6366f1" /></div>
          <div>
            <div style={S.summaryLabel}>총 회원</div>
            <div style={S.summaryValue}>{fmtNum(stats.users.total)}</div>
            <div style={S.summaryChange}><UserPlus size={12} /> 오늘 +{stats.users.todaySignups}</div>
          </div>
        </div>
        <div style={S.summaryCard}>
          <div style={{ ...S.summaryIcon, backgroundColor: '#f0fdf4' }}><ShoppingCart size={22} color="#10b981" /></div>
          <div>
            <div style={S.summaryLabel}>이번 달 주문</div>
            <div style={S.summaryValue}>{fmtNum(stats.orders.month)}</div>
            <div style={S.summaryChange}><Activity size={12} /> 오늘 {stats.orders.today}건</div>
          </div>
        </div>
        <div style={S.summaryCard}>
          <div style={{ ...S.summaryIcon, backgroundColor: '#fff7ed' }}><Handshake size={22} color="#f59e0b" /></div>
          <div>
            <div style={S.summaryLabel}>중개 요청</div>
            <div style={S.summaryValue}>{fmtNum(stats.brokerage.total)}</div>
            <div style={S.summaryChange}>성사율 {stats.brokerage.rate}%</div>
          </div>
        </div>
        <div style={S.summaryCard}>
          <div style={{ ...S.summaryIcon, backgroundColor: '#fef2f2' }}><DollarSign size={22} color="#ef4444" /></div>
          <div>
            <div style={S.summaryLabel}>이번 달 거래액</div>
            <div style={S.summaryValue}>{fmtAmount(stats.transactions.monthAmount)}</div>
            <div style={S.summaryChange}>총 {fmtAmount(stats.transactions.totalAmount)}</div>
          </div>
        </div>
      </div>

      {/* ===== 2. 사용자 상세 ===== */}
      <div style={S.cardGrid2}>
        <div style={S.card}>
          <h3 style={S.cardTitle}><Users size={18} /> 회원 현황</h3>
          <div style={S.statGrid}>
            <div style={S.statItem}>
              <span style={S.statLabel}>치과</span>
              <span style={{ ...S.statBadge, backgroundColor: '#eef2ff', color: '#6366f1' }}>{stats.users.dental}</span>
            </div>
            <div style={S.statItem}>
              <span style={S.statLabel}>기공소</span>
              <span style={{ ...S.statBadge, backgroundColor: '#f0fdf4', color: '#10b981' }}>{stats.users.lab}</span>
            </div>
            <div style={S.statItem}>
              <span style={S.statLabel}>외주업체</span>
              <span style={{ ...S.statBadge, backgroundColor: '#fff7ed', color: '#f59e0b' }}>{stats.users.outsource}</span>
            </div>
            <div style={S.statItem}>
              <span style={S.statLabel}>직원</span>
              <span style={{ ...S.statBadge, backgroundColor: '#faf5ff', color: '#8b5cf6' }}>{stats.users.staff}</span>
            </div>
            <div style={S.statItem}>
              <span style={S.statLabel}>개인회원</span>
              <span style={{ ...S.statBadge, backgroundColor: '#f1f5f9', color: '#64748b' }}>{stats.users.individual}</span>
            </div>
            <div style={S.statItem}>
              <span style={S.statLabel}>활성 (7일)</span>
              <span style={{ ...S.statBadge, backgroundColor: '#ecfdf5', color: '#059669' }}>{stats.users.activeUsers}</span>
            </div>
          </div>
          <div style={{ marginTop: 12, padding: '8px 12px', backgroundColor: '#f8fafc', borderRadius: 8, display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span>오늘 가입 <b>{stats.users.todaySignups}</b></span>
            <span>이번 주 <b>{stats.users.weekSignups}</b></span>
            <span>이번 달 <b>{stats.users.monthSignups}</b></span>
          </div>
        </div>

        {/* 월별 가입 추이 */}
        <div style={S.card}>
          <h3 style={S.cardTitle}><TrendingUp size={18} /> 월별 가입 추이</h3>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 160, padding: '10px 0' }}>
            {stats.users.monthlySignups.map((m, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#6366f1' }}>{m.count}</span>
                <div style={{ width: '100%', backgroundColor: '#6366f1', borderRadius: '4px 4px 0 0', height: `${Math.max((m.count / maxMonthlySignup) * 120, 4)}px`, transition: 'height .3s' }} />
                <span style={{ fontSize: 11, color: '#94a3b8' }}>{m.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== 3. 주문 상세 ===== */}
      <div style={S.cardGrid2}>
        <div style={S.card}>
          <h3 style={S.cardTitle}><ShoppingCart size={18} /> 주문 현황</h3>
          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <div style={S.miniCard}>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#0f172a' }}>{fmtNum(stats.orders.total)}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>전체 주문</div>
            </div>
            <div style={S.miniCard}>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#f59e0b' }}>{stats.orders.pending}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>대기</div>
            </div>
            <div style={S.miniCard}>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#3b82f6' }}>{stats.orders.inProgress}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>진행중</div>
            </div>
            <div style={S.miniCard}>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#10b981' }}>{stats.orders.completed}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>완료</div>
            </div>
          </div>
        </div>

        {/* 월별 주문 추이 */}
        <div style={S.card}>
          <h3 style={S.cardTitle}><TrendingUp size={18} /> 월별 주문 추이</h3>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 160, padding: '10px 0' }}>
            {stats.orders.monthlyOrders.map((m, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#10b981' }}>{m.count}</span>
                <div style={{ width: '100%', backgroundColor: '#10b981', borderRadius: '4px 4px 0 0', height: `${Math.max((m.count / maxMonthlyOrder) * 120, 4)}px`, transition: 'height .3s' }} />
                <span style={{ fontSize: 11, color: '#94a3b8' }}>{m.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== 4. 중개 & 거래 ===== */}
      <div style={S.cardGrid2}>
        <div style={S.card}>
          <h3 style={S.cardTitle}><Handshake size={18} /> 중개 현황</h3>
          <div style={S.statGrid}>
            <div style={S.statItem}>
              <span style={S.statLabel}>총 중개 요청</span>
              <span style={{ ...S.statBadge, backgroundColor: '#fff7ed', color: '#f59e0b' }}>{stats.brokerage.total}</span>
            </div>
            <div style={S.statItem}>
              <span style={S.statLabel}>이번 달</span>
              <span style={{ ...S.statBadge, backgroundColor: '#fff7ed', color: '#f59e0b' }}>{stats.brokerage.month}</span>
            </div>
            <div style={S.statItem}>
              <span style={S.statLabel}>성사 건수</span>
              <span style={{ ...S.statBadge, backgroundColor: '#f0fdf4', color: '#10b981' }}>{stats.brokerage.completed}</span>
            </div>
            <div style={S.statItem}>
              <span style={S.statLabel}>전환율</span>
              <span style={{ ...S.statBadge, backgroundColor: stats.brokerage.rate >= 50 ? '#f0fdf4' : '#fef2f2', color: stats.brokerage.rate >= 50 ? '#10b981' : '#ef4444' }}>{stats.brokerage.rate}%</span>
            </div>
          </div>
        </div>

        <div style={S.card}>
          <h3 style={S.cardTitle}><FileText size={18} /> 거래 현황</h3>
          <div style={S.statGrid}>
            <div style={S.statItem}>
              <span style={S.statLabel}>총 거래명세서</span>
              <span style={{ ...S.statBadge, backgroundColor: '#eef2ff', color: '#6366f1' }}>{stats.transactions.total}</span>
            </div>
            <div style={S.statItem}>
              <span style={S.statLabel}>이번 달</span>
              <span style={{ ...S.statBadge, backgroundColor: '#eef2ff', color: '#6366f1' }}>{stats.transactions.month}</span>
            </div>
            <div style={S.statItem}>
              <span style={S.statLabel}>총 거래액</span>
              <span style={{ ...S.statBadge, backgroundColor: '#fef2f2', color: '#ef4444' }}>{fmtAmount(stats.transactions.totalAmount)}</span>
            </div>
            <div style={S.statItem}>
              <span style={S.statLabel}>거래처 연결</span>
              <span style={{ ...S.statBadge, backgroundColor: '#f0fdf4', color: '#10b981' }}>{stats.transactions.connections}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ===== 5. 콘텐츠 현황 ===== */}
      <div style={S.card}>
        <h3 style={S.cardTitle}><Package size={18} /> 콘텐츠 현황</h3>
        <div style={S.contentGrid}>
          <div style={S.contentItem}>
            <Building2 size={20} color="#6366f1" />
            <div style={S.contentLabel}>기공소 홍보</div>
            <div style={S.contentValue}>{stats.content.labAds}</div>
            <div style={S.contentSub}>활성 {stats.content.labAdsActive}</div>
          </div>
          <div style={S.contentItem}>
            <Package size={20} color="#f59e0b" />
            <div style={S.contentLabel}>중고물품</div>
            <div style={S.contentValue}>{stats.content.usedItems}</div>
          </div>
          <div style={S.contentItem}>
            <Briefcase size={20} color="#8b5cf6" />
            <div style={S.contentLabel}>구인</div>
            <div style={S.contentValue}>{stats.content.jobs}</div>
          </div>
          <div style={S.contentItem}>
            <Calendar size={20} color="#3b82f6" />
            <div style={S.contentLabel}>세미나</div>
            <div style={S.contentValue}>{stats.content.seminars}</div>
          </div>
          <div style={S.contentItem}>
            <MessageCircle size={20} color="#10b981" />
            <div style={S.contentLabel}>피드백</div>
            <div style={S.contentValue}>{stats.content.feedbacks}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  cardGrid4: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 16 },
  cardGrid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 14, marginBottom: 16 },
  card: { backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 },
  cardTitle: { margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 },
  summaryCard: { backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20, display: 'flex', alignItems: 'center', gap: 16 },
  summaryIcon: { width: 52, height: 52, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  summaryLabel: { fontSize: 12, color: '#94a3b8', fontWeight: 600 },
  summaryValue: { fontSize: 24, fontWeight: 800, color: '#0f172a', lineHeight: 1.2 },
  summaryChange: { fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 },
  statGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  statItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', backgroundColor: '#f8fafc', borderRadius: 8 },
  statLabel: { fontSize: 13, color: '#475569' },
  statBadge: { padding: '3px 10px', borderRadius: 6, fontSize: 13, fontWeight: 700 },
  miniCard: { flex: 1, textAlign: 'center', padding: '14px 8px', backgroundColor: '#f8fafc', borderRadius: 10 },
  contentGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 },
  contentItem: { textAlign: 'center', padding: '16px 10px', backgroundColor: '#f8fafc', borderRadius: 10 },
  contentLabel: { fontSize: 12, color: '#64748b', marginTop: 6 },
  contentValue: { fontSize: 22, fontWeight: 800, color: '#0f172a', marginTop: 4 },
  contentSub: { fontSize: 11, color: '#94a3b8', marginTop: 2 },
  refreshBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};

export default AdminAnalytics;
